import React, { useState, useRef, useEffect } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import 'startbootstrap-sb-admin-2/css/sb-admin-2.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import '../../styles/admin-custom.css';

const NAV_ITEMS = [
  { path: '/organizer/dashboard',  icon: 'fa-tachometer-alt', label: 'Dashboard' },
  { path: '/organizer/events',     icon: 'fa-calendar',       label: 'Events' },
  { path: '/organizer/orders',     icon: 'fa-shopping-cart',  label: 'Orders' },
  { path: '/organizer/tickets',    icon: 'fa-ticket-alt',     label: 'Tickets' },
  { path: '/organizer/attendance', icon: 'fa-user-check',     label: 'Attendance' },
  { path: '/organizer/agents',     icon: 'fa-users',          label: 'Agents' },
  { path: '/organizer/financial',  icon: 'fa-dollar-sign',    label: 'Financial' },
];

const OrganizerLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [sidebarToggled, setSidebarToggled] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const handleLogout = async () => {
    await logout();
    navigate('/organizer/login');
  };

  const handleReturnToAdmin = () => {
    const adminToken = localStorage.getItem('admin_return_token');
    const adminUser  = localStorage.getItem('admin_return_user');
    if (adminToken && adminUser) {
      localStorage.setItem('token', adminToken);
      localStorage.setItem('user', adminUser);
      localStorage.setItem('userRole', 'admin');
      localStorage.removeItem('admin_impersonating');
      localStorage.removeItem('admin_return_token');
      localStorage.removeItem('admin_return_user');
      window.location.href = '/admin/dashboard';
    }
  };

  // Close user dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <div id="wrapper" className="organizer-layout">

      {/* ── Sidebar ─────────────────────────────────────────────────── */}
      <ul
        className={`navbar-nav bg-gradient-primary sidebar sidebar-dark accordion ${sidebarToggled ? 'toggled' : ''}`}
        id="accordionSidebar"
      >
        {/* Brand */}
        <Link
          className="sidebar-brand d-flex align-items-center justify-content-center py-4"
          to="/organizer/dashboard"
        >
          <div className="sidebar-brand-icon">
            <img
              src={`${process.env.PUBLIC_URL}/ticketaty-logo.svg`}
              alt="Ticketaty"
              style={{ height: '28px', width: 'auto', filter: 'brightness(0) invert(1)' }}
            />
          </div>
          {!sidebarToggled && (
            <div className="sidebar-brand-text mx-3 d-flex flex-column" style={{ lineHeight: 1.2 }}>
              <span style={{ fontSize: '0.95rem', fontWeight: 800, letterSpacing: '0.03em' }}>Ticketaty</span>
              <span style={{ fontSize: '0.65rem', fontWeight: 500, opacity: 0.75, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Organizer Portal</span>
            </div>
          )}
        </Link>

        <hr className="sidebar-divider my-0" />

        {/* Dashboard */}
        <li className={`nav-item ${isActive('/organizer/dashboard') ? 'active' : ''}`}>
          <Link className="nav-link" to="/organizer/dashboard" data-title="Dashboard">
            <i className="fas fa-fw fa-tachometer-alt"></i>
            <span>Dashboard</span>
          </Link>
        </li>

        <hr className="sidebar-divider" />
        <div className="sidebar-heading">Management</div>

        {NAV_ITEMS.slice(1).map(({ path, icon, label }) => (
          <li key={path} className={`nav-item ${isActive(path) ? 'active' : ''}`}>
            <Link className="nav-link" to={path} data-title={label}>
              <i className={`fas fa-fw ${icon}`}></i>
              <span>{label}</span>
            </Link>
          </li>
        ))}

        <hr className="sidebar-divider" />
        <div className="sidebar-heading">Account</div>

        <li className={`nav-item ${isActive('/organizer/settings') ? 'active' : ''}`}>
          <Link className="nav-link" to="/organizer/settings" data-title="Settings">
            <i className="fas fa-fw fa-cog"></i>
            <span>Settings</span>
          </Link>
        </li>

        <hr className="sidebar-divider d-none d-md-block" />

        {/* Collapse toggle */}
        <div className="text-center d-none d-md-inline pb-3">
          <button
            className="rounded-circle border-0"
            id="sidebarToggle"
            onClick={() => setSidebarToggled(!sidebarToggled)}
            title={sidebarToggled ? 'Expand sidebar' : 'Collapse sidebar'}
          />
        </div>
      </ul>
      {/* ── End Sidebar ─────────────────────────────────────────────── */}

      {/* ── Content Wrapper ─────────────────────────────────────────── */}
      <div id="content-wrapper" className="d-flex flex-column">
        <div id="content">

          {/* Topbar */}
          <nav className="navbar navbar-expand navbar-light bg-white topbar mb-4 static-top shadow">

            {/* Mobile toggle */}
            <button
              className="btn btn-link d-md-none rounded-circle mr-3"
              onClick={() => setSidebarToggled(!sidebarToggled)}
            >
              <i className="fa fa-bars"></i>
            </button>

            {/* Logo — shown in topbar when sidebar is collapsed */}
            {sidebarToggled && (
              <Link to="/organizer/dashboard" className="d-none d-md-flex align-items-center mr-3" style={{ textDecoration: 'none' }}>
                <img
                  src={`${process.env.PUBLIC_URL}/ticketaty-logo.svg`}
                  alt="Ticketaty"
                  style={{ height: '22px', width: 'auto' }}
                />
              </Link>
            )}

            {/* Right side */}
            <ul className="navbar-nav ml-auto align-items-center">

              {/* Return to Admin badge */}
              {localStorage.getItem('admin_impersonating') === 'true' && (
                <li className="nav-item mr-2">
                  <button
                    className="btn btn-sm btn-warning d-flex align-items-center gap-1"
                    onClick={handleReturnToAdmin}
                  >
                    <i className="fas fa-arrow-left fa-sm"></i>
                    <span className="d-none d-sm-inline ml-1">Return to Admin</span>
                  </button>
                </li>
              )}

              <div className="topbar-divider d-none d-sm-block"></div>

              {/* User dropdown */}
              <li className="nav-item dropdown no-arrow" ref={userMenuRef}>
                <button
                  className="btn btn-link nav-link d-flex align-items-center gap-2 px-3"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  style={{ textDecoration: 'none' }}
                >
                  <span className="d-none d-lg-inline text-gray-600 small font-weight-bold mr-1">{user?.name}</span>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      flexShrink: 0,
                    }}
                  >
                    {user?.name?.charAt(0)?.toUpperCase() || 'O'}
                  </div>
                </button>

                {userMenuOpen && (
                  <div
                    className="dropdown-menu dropdown-menu-right shadow animated--grow-in show"
                    style={{ minWidth: 200 }}
                  >
                    <div className="dropdown-header d-flex align-items-center gap-2 py-3 px-3">
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: '1rem',
                          flexShrink: 0,
                        }}
                      >
                        {user?.name?.charAt(0)?.toUpperCase() || 'O'}
                      </div>
                      <div style={{ lineHeight: 1.3 }}>
                        <div className="font-weight-bold text-gray-800" style={{ fontSize: '0.875rem' }}>{user?.name}</div>
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>{user?.email}</div>
                      </div>
                    </div>

                    <div className="dropdown-divider m-0"></div>

                    <button
                      className="dropdown-item py-2"
                      onClick={() => { setUserMenuOpen(false); navigate('/organizer/settings'); }}
                    >
                      <i className="fas fa-cog fa-sm fa-fw mr-2 text-gray-400"></i>
                      Settings
                    </button>

                    <div className="dropdown-divider"></div>

                    <button
                      className="dropdown-item text-danger py-2"
                      onClick={() => { setUserMenuOpen(false); handleLogout(); }}
                    >
                      <i className="fas fa-sign-out-alt fa-sm fa-fw mr-2"></i>
                      Logout
                    </button>
                  </div>
                )}
              </li>

            </ul>
          </nav>
          {/* End Topbar */}

          {/* Page Content */}
          <div className="container-fluid">
            <Outlet />
          </div>

        </div>

        {/* Footer */}
        <footer className="sticky-footer bg-white">
          <div className="container my-auto">
            <div className="copyright text-center my-auto d-flex align-items-center justify-content-center gap-2">
              <img
                src={`${process.env.PUBLIC_URL}/ticketaty-logo.svg`}
                alt="Ticketaty"
                style={{ height: '14px', width: 'auto', opacity: 0.4 }}
              />
              <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                &copy; {new Date().getFullYear()} Ticketaty. All rights reserved.
              </span>
            </div>
          </div>
        </footer>

      </div>
      {/* End Content Wrapper */}

    </div>
  );
};

export default OrganizerLayout;
